import Box from "@mui/material/Box";
import type {ColumnDef} from "@tanstack/react-table";
import type {PaymentMethod} from "crqlar-react-common/dist/models/billing/paymentMethod.model";
import type {Profile} from "crqlar-react-common/dist/models/profile/profile.model";
import {PaymentMethodService} from "crqlar-react-common/dist/services/billing/paymentMethod.service";
import {ProfileService} from "crqlar-react-common/dist/services/profile/profile.service";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {formatProfileListName} from "crqlar-react-common/dist/utils/profile.util";
import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {
    ServerTable,
    ServerTableColumns,
    ServerTableFrame,
    ServerTableFullscreen,
    ServerTableProvider,
    ServerTableSort,
    ServerTableToolbar,
    useServerTable
} from "../../../common/serverTable";
import SettingsListPage from "../../layout/SettingsListPage";

type PaymentMethodTableRow = PaymentMethod & {
    profileName: string
};

const PaymentMethodsList = () => {
    useTrackScreenVisit('system payment methods');

    const {t} = useTranslation();
    const navigate = useNavigate();

    const columns = useMemo<ColumnDef<PaymentMethodTableRow>[]>(() => [
        {
            id: 'profile',
            header: t('Profile'),
            accessorFn: paymentMethod => paymentMethod.profileName,
            cell: ({row}) => <Box>{row.original.profileName}</Box>,
            size: 320,
            meta: {defaultActive: true, grow: true}
        },
        {
            id: 'provider',
            header: t('Provider'),
            accessorFn: paymentMethod => paymentMethod.provider,
            cell: ({row}) => <Box>{row.original.provider || '-'}</Box>,
            size: 240,
            meta: {defaultActive: true, isServerSortable: true}
        },
        {
            id: 'type',
            header: t('Type'),
            accessorFn: paymentMethod => paymentMethod.type,
            cell: ({row}) => <Box>{row.original.type || '-'}</Box>,
            size: 240,
            meta: {defaultActive: true, isServerSortable: true}
        },
        {
            id: 'id',
            header: t('ID'),
            accessorFn: paymentMethod => paymentMethod.id,
            cell: ({row}) => <Box>{row.original.id}</Box>,
            size: 300,
            meta: {defaultActive: false, isServerSortable: true}
        }
    ], [t]);

    const controller = useServerTable({
        tableId: 'system-payment-methods',
        columns,
        getRowId: paymentMethod => paymentMethod.id!,
        onRowClick: paymentMethod => navigate(`/system/paymentMethods/${paymentMethod.id}`),
        initialSorting: [{id: 'id', desc: true}],
        queryFn: async ({pagination, sorting}) => {
            const page = await PaymentMethodService.getAllPaymentMethods({
                limit: {
                    offset: pagination.pageIndex * pagination.pageSize,
                    limit: pagination.pageSize,
                    sort: sorting ? [sorting] : undefined
                }
            });
            const paymentMethods = page.content ?? [];
            const profileIds = Array.from(new Set(paymentMethods
                .map(paymentMethod => paymentMethod.profileId)
                .filter((profileId): profileId is string => !!profileId)));
            const profiles = profileIds.length
                ? (await ProfileService.getAllProfiles({
                id: profileIds,
                limit: {
                    offset: 0,
                    limit: 1000,
                    sort: [{field: 'name', direction: 'ASC'}]
                }
            })).content ?? []
                : [];
            const profilesById = new Map<string, Profile>(profiles
                .filter((profile): profile is Profile & { id: string } => !!profile.id)
                .map(profile => [profile.id, profile]));

            return {
                content: paymentMethods.map<PaymentMethodTableRow>(paymentMethod => ({
                    ...paymentMethod,
                    profileName: paymentMethod.profileId
                        ? formatProfileListName(profilesById.get(paymentMethod.profileId)) || '-'
                        : '-'
                })),
                totalElements: page.totalElements ?? 0
            };
        }
    });

    return (
        <SettingsListPage title={t('Payment methods')} back={() => navigate('/system')}>
            <ServerTableProvider controller={controller}>
                <ServerTableFrame>
                    <ServerTableToolbar hoistOnCompact>
                        <ServerTableSort/>
                        <ServerTableFullscreen/>
                        <ServerTableColumns/>
                    </ServerTableToolbar>

                    <ServerTable/>
                </ServerTableFrame>
            </ServerTableProvider>
        </SettingsListPage>
    );
};

export default PaymentMethodsList;
